<template>
    <div
        :class="[$style['mds-popover'], classObject, positionClass, widthClass, sizeClass]"
        role="tooltip"
        :aria-hidden="isVisible ? 'false' : 'true'"
        :data-mds-version="version"
    >
        <div :class="$style['mds-popover__content']">
            <slot></slot>
        </div>
    </div>
</template>

<script>
    import { MdsPositioning, MdsTrapFocus, positions } from '@mds/utils-js';
    import packageInfo from '../package.json';
    const { name, version } = packageInfo;

    export default {
        name: 'MdsPopover',
        props: {
            actionRequired: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description:
                        'If `true`, suppresses the ability to dismiss the popover with the esc key or by clicking the underlying page. An action in the popover must be used to dismiss.',
                },
            },
            disableFocusTrap: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description:
                        'If `true`, suppresses the built-in focus trap logic allowing you to leverage your own or third-party focus management.',
                },
            },
            fixed: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description:
                        'When a popover is triggered from an element or container set to `position: fixed;`, this prop must be set `true` to ensure proper positioning.',
                },
            },
            position: {
                type: Array,
                default: () => positions,
                validator: value => value.every(val => positions.includes(val)),
                docs: {
                    validation:
                        'One or more of the following: `bottom-left`, `bottom-center`, `bottom-right`, `left-center`, `right-center`, `top-left`, `top-center`, `top-right`.',
                    description:
                        'If a single value, sets the position of the popover. If multiple values, the first value sets the initial position while the rest define the auto-position order.',
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium', 'large'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `small`, `medium`, `large`.',
                    description: 'Sets the size of the popover.',
                },
            },
            triggeredBy: {
                type: String,
                required: true,
                docs: {
                    validation: 'Required',
                    description: 'The `id` of the popover’s trigger element.',
                },
            },
            visible: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'Sets visibility of the popover.',
                    support: ['v-model'],
                },
            },
            width: {
                type: String,
                default: '300px',
                validator(value) {
                    return ['200px', '300px', '500px'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of `200px`, `300px`, `500px`.',
                    description: 'Sets the width of the popover.',
                },
            },
        },
        mixins: [MdsPositioning, MdsTrapFocus],
        data() {
            return {
                isVisible: false,
                currentPosition: this.position[0],
                triggerElement: null,
                version: `${name}-${version}`,
            };
        },
        model: {
            prop: 'visible',
            event: 'mds-popover-dismissed',
        },
        watch: {
            visible(visible) {
                this[visible ? 'show' : 'hide']();
            },
        },
        computed: {
            classObject() {
                const obj = {};
                obj[this.$style['mds-popover--visible']] = this.isVisible;
                obj[this.$style['mds-popover--fixed']] = this.fixed;

                return obj;
            },
            positionClass() {
                return this.$style[`mds-popover--${this.currentPosition}`];
            },
            widthClass() {
                return this.$style[`mds-popover--width-${this.width}`];
            },
            autoPosition() {
                return this.position.length > 1;
            },
            sizeClass() {
                return this.$style[`mds-popover--${this.size}`];
            },
        },
        methods: {
            updatePosition() {
                return new Promise(resolve => {
                    const { setPosition, attributes } = this.getPositionAttributes();
                    this.currentPosition = setPosition;
                    this.$el.setAttribute('style', this.formatPositionStyleAttributes(attributes));

                    this.$emit('mds-popover-position-updated');

                    resolve();
                }).catch(error => error);
            },
            show(event) {
                if (!this.disableFocusTrap) {
                    this.enableTrapFocus();
                }
                this.$nextTick(function() {
                    this.updatePosition().then(() => {
                        this.isVisible = true;
                        this.$emit('mds-popover-opened', event);
                    });
                });
            },
            hide(event) {
                // to negate duplicate dismiss event emit
                if (!this.isVisible) {
                    return;
                }

                this.isVisible = false;

                if (!this.disableFocusTrap) {
                    this.disableTrapFocus();
                }

                this.$emit('mds-popover-dismissed', this.isVisible, event);
            },
            bindEventHandlers() {
                if (this.triggerElement) {
                    window.addEventListener('keydown', this.keyboardEventHandler, true);
                    window.addEventListener('click', this.bodyEventHandler, true);
                    window.addEventListener('resize', this.debounce(this.updatePosition, 100));

                    if (this.autoPosition) {
                        window.addEventListener('scroll', this.debounce(this.updatePosition, 100));
                    }
                }
            },
            keyboardEventHandler(event) {
                // Keyboard check if key is Esc
                if (event.keyCode === 27 && !this.actionRequired) {
                    this.hide();
                }
            },
            bodyEventHandler(event) {
                if (this.actionRequired) {
                    return;
                }
                const popover = this.$el;
                let targetElement = event.target;

                // Suppress dismissal of popover when the popover is clicked
                do {
                    if (targetElement === popover || targetElement === this.triggerElement) {
                        return;
                    }
                    targetElement = targetElement.parentNode;
                } while (targetElement);

                this.hide();
            },
            removeEventHandlers() {
                if (this.triggerElement) {
                    window.removeEventListener('keydown', this.keyboardEventHandler, true);
                    window.removeEventListener('click', this.bodyEventHandler, true);
                    window.removeEventListener('resize', this.updatePosition, true);

                    if (this.autoPosition) {
                        window.removeEventListener('scroll', this.updatePosition, true);
                    }

                    this.$emit('mds-popover-listeners-destroyed');
                }
            },
        },
        mounted() {
            document.body.appendChild(this.$el);
            this.triggerElement = document.getElementById(`${this.triggeredBy}`);

            this.bindEventHandlers();

            this.$nextTick(function() {
                this.updatePosition().then(() => (this.isVisible = this.visible));
            });
        },
        beforeDestroy() {
            this.removeEventHandlers();

            if (this.$el.parentNode) {
                this.$el.parentNode.removeChild(this.$el);
            }
        },
    };
</script>

<style lang="scss" module>
    @import './popover.scss';
</style>
